import { EventInteraction, EventInteractionContext, EventInteractions } from '@/src/homepage/utils/tracking';

/**
 * Marks every PicoX payload built by this module as originating in the
 * browser, so the analytics pipeline can distinguish frontend-emitted ad
 * events from server-side equivalents.
 */
export const PICOX_TRACKING_ORIGIN = 'frontend';

// Browser/device context (user_agent, locale, timezone, viewport, landing URL +
// utm/click attribution, etc.) is captured automatically by the PicoX SDK
// (`@eventbrite/bsp-sdk`'s context provider). We deliberately only emit
// ad-domain fields here.

/**
 * Canonical PicoX event names emitted by the ads tracking pipeline.
 *
 * Ported from eb-ui `packages/attendee/ads/src/tracking/picoxInteractionsMapper.ts`
 * (PR eventbrite/eb-ui#36290) and kept byte-identical with listings-webapp's copy
 * so the spend pipeline attributes promoted impressions / clicks uniformly
 * regardless of which frontend served the page.
 *
 * `PromotedEventImpression` / `PromotedEventClick` fire per promoted card and
 * power the spend pipeline (paired with `ad_bid`).
 */
export const PicoXAdsEventNames = {
    PromotedEventImpression: 'ads_promoted_eb_event_impression',
    PromotedEventClick: 'ads_promoted_eb_event_click',
    RelatedEventsClicked: 'related_eb_events_clicked',
    RelatedEventsRecommended: 'related_eb_events_recommended',
} as const;

/**
 * Previous wire name for the related-events click, kept while dashboards and
 * running experiments still read it.
 */
export const DEPRECATED_RELATED_EVENTS_CLICKED_NAME = 'eb_event_page_opened';

/** Scalar primitives PicoX accepts inside a payload field. */
type PicoXPrimitive = string | number | boolean | null;

/**
 * Any value a PicoX payload field can hold: a primitive or an array of
 * primitives. The tracker rejects nested objects, so payloads stay flat
 * and easy to query in the downstream warehouse.
 */
export type PicoXPayloadValue = PicoXPrimitive | PicoXPrimitive[];

/**
 * Whether a recommended event came from the ad auction (`ads`) or organic
 * ranking (`organic`). Reported so analytics can split spend-bearing
 * impressions from free recommendations.
 */
type PicoXRecommendationSource = 'ads' | 'organic';

/**
 * Wire shape of `ads_promoted_eb_event_impression` and `_click`. Carries
 * the impressed slot's identity (`eb_event_id` / `parent_eb_event_id`,
 * both private IDs), auction metadata (`ad_id`, `campaign_id`, `ad_bid`,
 * `placement_id`), surface coordinates (`rank`, `page`,
 * `product_location`), and a few denormalized event traits
 * (`is_online`, `is_free`, `has_urgency_signals`) the spend pipeline
 * indexes on for reporting.
 */
export type PicoXPromotedEventPayload = {
    tracking_origin: typeof PICOX_TRACKING_ORIGIN;
    product_location: string;
    sub_product_location: string | null;
    eb_event_source: PicoXRecommendationSource;
    ad_id: string | null;
    campaign_id: string | number | null;
    eb_event_id: string;
    parent_eb_event_id: string | null;
    ad_bid: number | null;
    placement_id: string | null;
    rank: number;
    absolute_rank: number | null;
    page: number;
    position: string | null;
    action: EventInteractions;
    is_online: boolean;
    is_free: boolean;
    has_urgency_signals: boolean;
    venue_id: string | null;
};

/**
 * Minimal shape of a recommended event the PicoX builders consume. Accepts
 * both `promotedListingMetadata` (camelCase, frontend internal) and
 * `promoted_listing_metadata` (snake_case, the raw Ads Retrieval / BFF shape)
 * so callers can pass through whichever form the surface holds.
 */
export type PicoXRecommendedEvent = {
    id: string | number;
    isPromoted?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    promotedListingMetadata?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    promoted_listing_metadata?: any;
};

/**
 * Discriminated event envelope produced by
 * `mapInteractionToPicoXPromotedEvent`: a PicoX event name paired with a
 * generic-shaped payload, ready to hand directly to `usePicoXTrack`
 * without further branching at the call site.
 */
export type PicoXPromotedEvent = {
    name: (typeof PicoXAdsEventNames)[keyof typeof PicoXAdsEventNames];
    payload: Record<string, PicoXPayloadValue>;
};

/**
 * Maps an `EventInteraction` action to the matching PicoX event name.
 * Only impressions and clicks have billable PicoX equivalents today;
 * other actions return `null` so the caller can short-circuit instead of
 * emitting an unattributed event.
 */
const getPicoXEventName = (action: EventInteractions): PicoXPromotedEvent['name'] | null => {
    switch (action) {
        case EventInteractions.Impression:
            return PicoXAdsEventNames.PromotedEventImpression;
        case EventInteractions.Click:
            return PicoXAdsEventNames.PromotedEventClick;
        default:
            return null;
    }
};

/**
 * Pulls promoted-listing metadata off an event regardless of whether the
 * surface stored it in camelCase (frontend) or snake_case (Ads Retrieval).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getPromotedMetadata = (event: any = {}) => {
    return event.promotedListingMetadata || event.promoted_listing_metadata || {};
};

/**
 * Translates a public event ID into the private form PicoX expects (public
 * ID divided by 1003 — Eventbrite's longstanding ID obfuscation factor).
 * Returns `null` for nullish input and falls back to the original string
 * when the input isn't numeric so callers don't lose non-standard IDs.
 */
export const toPrivateEventId = (
    publicEventId: string | number | null | undefined,
): string | null => {
    if (publicEventId == null) {
        return null;
    }

    const eventId = String(publicEventId);
    const numericEventId = Number(eventId);

    if (!Number.isFinite(numericEventId)) {
        return eventId;
    }

    return String(numericEventId / 1003);
};

/**
 * Labels each surfaced event as `ads` (auction-served) or `organic` so the
 * downstream pipeline can split spend-bearing impressions from free
 * recommendations when reporting.
 */
const getEventSource = (event: PicoXRecommendedEvent): PicoXRecommendationSource =>
    event.isPromoted ? 'ads' : 'organic';

/**
 * Reads a single promoted-metadata field, accepting both the camelCase
 * (frontend-internal) and snake_case (Ads Retrieval BFF) variants so callers
 * stay agnostic to which surface produced the event. Coerces non-primitive
 * values to `null` to keep PicoX payloads schema-friendly.
 */
const getPromotedMetadataValue = (
    event: PicoXRecommendedEvent,
    camelKey: string,
    snakeKey: string,
): string | number | null => {
    const promotedMetadata = getPromotedMetadata(event);
    const value = promotedMetadata[camelKey] ?? promotedMetadata[snakeKey];

    return typeof value === 'string' || typeof value === 'number' ? value : null;
};

/**
 * Surfaces the ad ID of a promoted slot, coerced to string because PicoX
 * stringifies all ID fields. Returns `null` for organic events so the
 * caller can omit the field rather than emit a misattributed ID.
 */
const getEventAdId = (event: PicoXRecommendedEvent): string | null => {
    const adId = getPromotedMetadataValue(event, 'adId', 'ad_id');

    return adId == null ? null : String(adId);
};

/**
 * Resolves the placement ID for a promoted impression as a `string`. Reads
 * from `promotedListingMetadata` first (the BFF surface sometimes delivers
 * `placement_id` as a number), and falls back to the place-level
 * `placementId` legacy surfaces attach. PicoX rejects non-string IDs, so
 * any non-nullish value is run through `String(...)` before being emitted.
 */
const getEventPlacementId = (
    event: PicoXRecommendedEvent,
    fallback?: string | number | null,
): string | null => {
    const placementId = getPromotedMetadataValue(event, 'placementId', 'placement_id') ?? fallback;

    return placementId == null || placementId === '' ? null : String(placementId);
};

/**
 * Surfaces the campaign ID an attendee saw, reading from promoted-listing
 * metadata in whichever case the upstream payload used. Used to attribute
 * impressions/clicks back to the auction campaign in the spend pipeline.
 */
export const getEventCampaignId = (event: PicoXRecommendedEvent): string | number | null =>
    getPromotedMetadataValue(event, 'campaignId', 'campaign_id');

/**
 * Resolves the campaign ID for an interaction by checking the event's
 * promoted metadata first and falling back to the place-level context
 * older surfaces attach. This keeps attribution working on legacy
 * call-sites that haven't moved campaign ID onto the event itself.
 */
const getCampaignId = (
    interaction: EventInteraction<EventInteractionContext>,
): string | number | null => {
    return (
        getEventCampaignId(interaction.event) ??
        interaction.place.context?.campaign_id ??
        interaction.place.context?.campaignId ??
        null
    );
};

/**
 * Coerces a raw bid value (upstream APIs frequently deliver bids as
 * strings) into a finite `number` so PicoX receives a clean numeric
 * `ad_bid`. Returns `null` for missing or non-numeric input rather than
 * `NaN`, which would corrupt the downstream spend aggregation.
 */
const normalizeAdBid = (value: string | number | null): number | null => {
    if (value == null || value === '') {
        return null;
    }

    const numericBid = Number(value);

    return Number.isFinite(numericBid) ? numericBid : null;
};

/**
 * Surfaces the bid associated with a promoted impression as `ad_bid` on
 * PicoX payloads — used downstream by the spend pipeline as a proxy for
 * what the auction charged. Prefers the auction's goal-adjusted bid (what
 * Insert returns as `goal_adj_bid` after applying campaign objective
 * multipliers) and falls back to the raw `ad_bid` for legacy payloads where
 * Insert hasn't yet attached the goal-adjusted value.
 */
export const getEventAdBid = (event: PicoXRecommendedEvent): number | null =>
    normalizeAdBid(
        getPromotedMetadataValue(event, 'goalAdjBid', 'goal_adj_bid') ??
            getPromotedMetadataValue(event, 'adBid', 'ad_bid'),
    );

/**
 * Resolves the private ID of the event page that drove a recommendation,
 * checking every key shape the various tracking surfaces have produced
 * (related_event / relatedEvent objects, *_id flat keys, parent_event
 * aliases). Returns `null` when no parent context is present so PicoX can
 * omit the field rather than report a misattributed parent.
 */
const getParentEventId = (
    interaction: EventInteraction<EventInteractionContext>,
): string | null => {
    return (
        toPrivateEventId(
            interaction.place.context?.related_event?.id ??
                interaction.place.context?.relatedEvent?.id ??
                interaction.place.context?.related_event_id ??
                interaction.place.context?.relatedEventId ??
                interaction.place.context?.parent_event_id ??
                interaction.place.context?.parentEventId,
        ) ?? null
    );
};

/**
 * Builds the payload for `ads_promoted_eb_event_impression` /
 * `ads_promoted_eb_event_click`, fired when a promoted card is viewed or
 * clicked. `eb_event_id` and `parent_eb_event_id` are emitted as private IDs
 * (public ID / 1003) because PicoX standardizes on private IDs, and `ad_bid`
 * carries the auction goal-adjusted bid surfaced by Insert so the spend
 * pipeline can attribute cost to each impression/click. `ad_id` and
 * `placement_id` are run through string coercers because the BFF can
 * deliver them as numbers and PicoX validates these as `String`.
 */
export const buildPicoXPromotedEventPayload = (
    interaction: EventInteraction<EventInteractionContext>,
): PicoXPromotedEventPayload => {
    const campaignId = getCampaignId(interaction);
    const urgencySignals = interaction.displayContext?.urgencySignals || [];
    const source = getEventSource(interaction.event);
    const privateEventId = toPrivateEventId(interaction.id) || interaction.id;

    return {
        tracking_origin: PICOX_TRACKING_ORIGIN,
        product_location: interaction.place.name,
        sub_product_location: interaction.place.subInterface?.name || null,
        eb_event_source: source,
        ad_id:
            getEventAdId(interaction.event) ?? (interaction.adId ? String(interaction.adId) : null),
        campaign_id: campaignId,
        eb_event_id: privateEventId,
        parent_eb_event_id: getParentEventId(interaction),
        ad_bid: getEventAdBid(interaction.event),
        placement_id: getEventPlacementId(interaction.event, interaction.place.placementId),
        rank: interaction.place.position,
        absolute_rank: interaction.place.absoluteRank ?? null,
        page: interaction.place.page,
        position: interaction.place.subInterface?.position || null,
        action: interaction.action,
        is_online: Boolean(interaction.event.isOnline ?? interaction.event.isOnlineEvent),
        is_free: Boolean(interaction.event.isFree),
        has_urgency_signals: urgencySignals.length > 0,
        venue_id: interaction.venueId || null,
    };
};

/**
 * Bridges the generic `EventInteraction` event-bus signal into a concrete
 * PicoX event for the spend pipeline. Returns `null` for organic events
 * (only promoted impressions/clicks are billable) and for action types we
 * don't track yet, so the call site can short-circuit without branching.
 */
export const mapInteractionToPicoXPromotedEvent = (
    interaction: EventInteraction<EventInteractionContext>,
): PicoXPromotedEvent | null => {
    if (!interaction.event?.isPromoted) {
        return null;
    }

    const name = getPicoXEventName(interaction.action);

    if (!name) {
        return null;
    }

    return {
        name,
        payload: buildPicoXPromotedEventPayload(interaction) as Record<string, PicoXPayloadValue>,
    };
};
